import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { demeterEmailSubscriptionsApi } from '../../../Apis/Apis';
import { DemeterEmailSubscriptionModel, DemeterEmailSubscriptionType } from '../../../Generated/Raven-Demeter';
import useApi from '../../Apis/Hooks/useApiHook';
import PageHeaderWithBackNavigation from '../../Components/Headers/PageHeaderWithBackNavigation';
import PageLoadingSpinner from '../../Components/LoadingSpinner/PageLoadingSpinner';
import Table from '../../Components/Tables/Table/Table';
import { TableHeader } from '../../Components/Tables/Table/TableDefinitions';
import useLanguage from '../../Services/Language/useLanguageHook';
import EmailSubscriptionActions from './EmailSubscriptionActions';
import styles from './EmailSubscriptionsPage.module.scss';

const EmailSubscriptionsPage: React.FC = () => {
    const [translations] = useLanguage();
    const navigate = useNavigate();

    const [listDemeterSubscriptionsLoading, listDemeterSubscriptions, listDemeterSubscriptionsResponse] = useApi(() =>
        demeterEmailSubscriptionsApi.listDemeterEmailSubscriptions(),
    );

    const groupedSubscriptions = useMemo(() => {
        const rows = listDemeterSubscriptionsResponse?.rows ?? [];
        const map = new Map<DemeterEmailSubscriptionType, typeof rows>();

        rows.forEach((r) => {
            const key = r.emailSubscriptionType as DemeterEmailSubscriptionType;
            const arr = map.get(key) ?? [];
            arr.push(r);
            map.set(key, arr);
        });

        const sortKey = (k: DemeterEmailSubscriptionType) => {
            switch (k) {
                case DemeterEmailSubscriptionType.DairyInsurancePriceSubscription:
                    return 0;
                case DemeterEmailSubscriptionType.LivestockInsurancePriceSubscription:
                    return 1;
                default:
                    return 2;
            }
        };
        return Array.from(map.entries()).sort((a, b) => sortKey(a[0]) - sortKey(b[0]));
    }, [listDemeterSubscriptionsResponse]);

    const navigateBack = () => {
        navigate(-1);
    };

    const actionsCellRenderer = (parameters: DemeterEmailSubscriptionModel) => (
        <EmailSubscriptionActions subscription={parameters} handleChange={listDemeterSubscriptions} />
    );

    const tableDefinitions: TableHeader<DemeterEmailSubscriptionModel>[] = [
        {
            field: 'name',
            header: translations.words.name,
            minimumWidth: 150,
        },
        {
            field: 'actions',
            header: translations.words.actions,
            width: 180,
            cellRenderer: actionsCellRenderer,
        },
    ];

    const isLoading = listDemeterSubscriptionsLoading || !listDemeterSubscriptionsResponse;

    return (
        <div className={styles.master_page_container}>
            <PageHeaderWithBackNavigation
                title={translations.subscriptions.title}
                testId="EmailSubscriptionsPageHeader"
                handleBackNavigation={navigateBack}
                track={{ eventCategory: 'EmailSubscriptions', eventAction: 'BackClicked' }}
            />
            {isLoading ? (
                <PageLoadingSpinner />
            ) : (
                <div className={styles.email_subscriptions_main_container}>
                    {groupedSubscriptions.map(([subscriptionType, rows]) => (
                        <div className={styles.email_subscriptions_group} key={subscriptionType}>
                            <h2>{translations.subscriptionType[subscriptionType]}</h2>
                            <div>
                                <Table tableData={rows} tableHeaders={tableDefinitions} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
export default EmailSubscriptionsPage;
